import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@urc-ui/store/types';
import type { ReportFilters } from '@flexible-reports/store/slices/types';
import type { FieldValidationError } from '@flexible-reports/store/slices/report-form/validation/types';
import { ServerValidationError } from '@flexible-reports/store/api/server-error-codes';

// ============================================================
// TYPE DEFINITIONS
// ============================================================
interface Template {
  templateId: string;
  name: string;
  description: string;
}

export type TemplateOptionSelections = Record<string, string[]>;

export type ReportPeriod = {
  preset?: string;
  start?: string;
  end?: string;
  reportingTimezone?: string;
};

export type AnalysisConfig = {
  enabled: boolean;
  metadataId: string | null;
};

export type TemplateGroupData = string | string[];

export interface TemplateWorkflowState {
  selectedTemplate: Template | null;
  reportName: string;
  reportFilters: ReportFilters;
  reportPeriod: ReportPeriod;
  format: 'CSV' | 'EXCEL';
  analysisConfig: AnalysisConfig;
  templateGroups: Record<string, TemplateGroupData>;
  templateOptionSelections: TemplateOptionSelections;
  fieldValidationErrors: FieldValidationError[];
  submitErrors: ServerValidationError[];
}

// ============================================================
// INITIAL STATE
// ============================================================
const getInitialState = (): TemplateWorkflowState => ({
  selectedTemplate: null,
  reportName: '',
  reportFilters: {
    managerAccountIds: [],
    advertiserAccountIds: [],
    countries: [],
    campaignIds: [],
    useAllCampaigns: true,
    productTypes: []
  },
  reportPeriod: {
    preset: 'LAST_7_DAYS',
    reportingTimezone: ''
  },
  format: 'CSV',
  analysisConfig: {
    enabled: false,
    metadataId: null
  },
  templateGroups: {},
  templateOptionSelections: {},
  fieldValidationErrors: [],
  submitErrors: []
});

// ============================================================
// SLICE
// ============================================================
const templateWorkflowSlice = createSlice({
  name: 'templateWorkflow',
  initialState: getInitialState(),
  reducers: {
    openSheet: (state, action: PayloadAction<{ template: Template; analysisMetadataId?: string }>) => {
      const { template, analysisMetadataId } = action.payload;
      Object.assign(state, getInitialState());
      state.selectedTemplate = template;
      state.analysisConfig = {
        enabled: !!analysisMetadataId,
        metadataId: analysisMetadataId || null
      };
    },

    closeSheet: state => {
      Object.assign(state, getInitialState());
    },

    setManagerAccountsFilter: (state, action: PayloadAction<string[]>) => {
      state.reportFilters.managerAccountIds = action.payload;
    },

    setAdvertisersFilter: (state, action: PayloadAction<string[]>) => {
      state.reportFilters.advertiserAccountIds = action.payload;
    },

    setCampaignIdsFilter: (state, action: PayloadAction<string[]>) => {
      state.reportFilters.campaignIds = action.payload;
    },

    setUseAllCampaigns: (state, action: PayloadAction<boolean>) => {
      state.reportFilters.useAllCampaigns = action.payload;
      if (action.payload) {
        state.reportFilters.campaignIds = [];
      }
    },

    setCampaignCountryFilter: (state, action: PayloadAction<string[]>) => {
      state.reportFilters.countries = action.payload;
    },

    setProductTypeFilter: (state, action: PayloadAction<string[]>) => {
      state.reportFilters.productTypes = action.payload;
    },

    setReportPeriod: (state, action: PayloadAction<ReportPeriod>) => {
      state.reportPeriod = action.payload;
    },

    setFormat: (state, action: PayloadAction<'CSV' | 'EXCEL'>) => {
      state.format = action.payload;
    },

    setReportName: (state, action: PayloadAction<string>) => {
      state.reportName = action.payload;
    },

    setTemplateOptionSelections: (state, action: PayloadAction<{ optionId: string; values: string[] }>) => {
      const { optionId, values } = action.payload;
      state.templateOptionSelections[optionId] = values;
    },

    setTemplateGroup: (state, action: PayloadAction<{ groupId: string; data: TemplateGroupData }>) => {
      const { groupId, data } = action.payload;
      state.templateGroups[groupId] = data;
    },

    setFieldValidationErrors: (state, action: PayloadAction<FieldValidationError[]>) => {
      state.fieldValidationErrors = action.payload;
    },

    setSubmitErrors: (state, action: PayloadAction<ServerValidationError[]>) => {
      state.submitErrors = action.payload;
    },

    clearErrors: state => {
      state.fieldValidationErrors = [];
      state.submitErrors = [];
    }
  }
});

// ============================================================
// ACTIONS
// ============================================================
export const {
  openSheet,
  closeSheet,
  setManagerAccountsFilter,
  setAdvertisersFilter,
  setCampaignIdsFilter,
  setUseAllCampaigns,
  setCampaignCountryFilter,
  setProductTypeFilter,
  setReportPeriod,
  setFormat,
  setReportName,
  setTemplateOptionSelections,
  setTemplateGroup,
  setFieldValidationErrors,
  setSubmitErrors,
  clearErrors
} = templateWorkflowSlice.actions;

// ============================================================
// SELECTORS
// ============================================================
export const selectTemplateWorkflowState = (state: RootState) => state.templateWorkflow;
export const selectSelectedTemplate = (state: RootState) => state.templateWorkflow.selectedTemplate;
export const selectReportName = (state: RootState) => state.templateWorkflow.reportName;
export const selectReportFilters = (state: RootState) => state.templateWorkflow.reportFilters;
export const selectReportPeriod = (state: RootState) => state.templateWorkflow.reportPeriod;
export const selectFormat = (state: RootState) => state.templateWorkflow.format;
export const selectAnalysisConfig = (state: RootState) => state.templateWorkflow.analysisConfig;
export const selectTemplateOptionSelections = (state: RootState) => state.templateWorkflow.templateOptionSelections;
export const selectTemplateOptionSelection = (optionId: string) => (state: RootState) =>
  state.templateWorkflow.templateOptionSelections[optionId] || [];
export const selectTemplateGroup = (groupId: string) => (state: RootState) => state.templateWorkflow.templateGroups[groupId];
export const selectFieldValidationErrors = (state: RootState) => state.templateWorkflow.fieldValidationErrors;
export const selectSubmitErrors = (state: RootState) => state.templateWorkflow.submitErrors;

// Composite selectors for FilterPicker compatibility
export const selectManagerAccountsFilter = createSelector([selectReportFilters], filters => filters.managerAccountIds);
export const selectAdvertisersFilter = createSelector([selectReportFilters], filters => filters.advertiserAccountIds);
export const selectCampaignIdsFilter = createSelector([selectReportFilters], filters => filters.campaignIds);
export const selectUseAllCampaigns = createSelector([selectReportFilters], filters => filters.useAllCampaigns);
export const selectCampaignCountryFilter = createSelector([selectReportFilters], filters => filters.countries);
export const selectProductTypeFilter = createSelector([selectReportFilters], filters => filters.productTypes);
export const createTemplateFieldValidationErrorsSelector = (fieldPath: string[]) =>
  createSelector([selectFieldValidationErrors], errors =>
    errors.filter(error => error.path.length === fieldPath.length && error.path.every((segment, i) => segment === fieldPath[i]))
  );

export default templateWorkflowSlice.reducer;
